import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import "./LineGraphTwoLine.css";
import { Box } from "@mui/material";
import { grey } from "@mui/material/colors";

const LineGraphTwoLine = ({ column = {}, data = {}, width = 500, height, label }) => {
	const { x, y, l1, l2 } = column;
	const margin = { top: 10, right: 10, bottom: 10, left: 30 };
	const calculatedWidth = width - margin.left - margin.right;
	const heightBasedOnWidth = width - Math.round(width / 2);
	const calculatedHeight = (height ? height : heightBasedOnWidth) - margin.top - margin.bottom;

	const svgRef = useRef();
	const tooltipRef = useRef();

	useEffect(() => {
		const svg = d3.select(svgRef.current);
		const tooltip = d3.select(tooltipRef.current);

		svg.selectAll("*").remove(); // Clear previous render
		const chart = svg.attr("viewBox", `0 0 ${calculatedWidth} ${calculatedHeight}`).append("g");
		// .attr("transform", `translate(${margin.left},${margin.top})`);

		const xScale = d3
			.scalePoint()
			.domain(data.map((d) => d[x.dataCol]))
			.range([margin.left, calculatedWidth])
			.padding(0.8);
		const yScale = d3
			.scaleLinear()
			.domain([0, d3.max(data, (d) => Math.max(d[l2.dataCol] ?? 0, d[l1.dataCol] ?? 0)) * 1.2])
			.nice()
			.range([calculatedHeight, margin.bottom]);

		// Axes
		// chart.append("g").attr("transform", `translate(0, ${height})`).call(d3.axisBottom(x));
		chart
			.append("g")
			.attr("transform", `translate(0, ${calculatedHeight - margin.top - margin.bottom})`)
			.call(d3.axisBottom(xScale))
			.call((g) => {
				g.select(".domain").remove(); // remove bottom axis line
				g.selectAll(".tick line").remove(); // remove vertical tick lines (|)
				g.selectAll(".tick text") // increase font size
					.style("font-size", "12px")
					.style("fill", "#000") // optional: soften color
					.style("text-transform", "uppercase");
			}); // chart.append("g").call(d3.axisLeft(y));
		svg.append("foreignObject")
			.attr("x", 0)
			.attr("y", 0)
			.attr("width", margin.left)
			.attr("height", height ?? heightBasedOnWidth)
			.append("xhtml:div")
			.style("display", "flex")
			.style("align-items", "center")
			.style("justify-content", "center")
			.style("height", "100%")
			.style("background", "black")
			.style("color", "white")
			.style("padding", "4px")
			.style("writing-mode", "sideways-lr")
			.style("text-orientation", "mixed")
			.style("font-size", "15")
			.text(y.label ?? y.dataCol);
		// Line generators
		const lineTY = d3
			.line()
			.x((d) => xScale(d[x.dataCol]))
			.y((d) => yScale(d[l2.dataCol]));

		const lineLY = d3
			.line()
			.x((d) => xScale(d[x.dataCol]))
			.y((d) => yScale(d[l1.dataCol]));

		// Draw lines
		chart.append("path").datum(data).attr("d", lineLY).attr("fill", "none").attr("stroke", l1.color).attr("stroke-width", 1.2);
		chart.append("path").datum(data).attr("d", lineTY).attr("fill", "none").attr("stroke", l2.color).attr("stroke-width", 1.2);

		// Add Hover Elements
		const focusLine = chart
			.append("line")
			.style("stroke", grey[300])
			// .style("stroke-", "3 3")
			.style("opacity", 0)
			.attr("y1", 0)
			.attr("y2", calculatedHeight);

		const focusDotLY = chart.append("circle").attr("r", 3).attr("fill", l1.color).style("opacity", 0);
		const focusDotTY = chart.append("circle").attr("r", 3).attr("fill", l2.color).style("opacity", 0);

		// Add overlay for hover
		chart
			.append("rect")
			.attr("width", calculatedWidth)
			.attr("height", calculatedHeight - margin.bottom)
			.style("fill", "none")
			.style("pointer-events", "all")
			.on("mousemove", function (event) {
				const [xm] = d3.pointer(event);
				const i = d3.bisector((d) => xScale(d[x.dataCol])).center(data, xm);
				const d = data[i];

				if (!d) return;

				const xCoord = xScale(d[x.dataCol]);
				const yTY = yScale(d[l2.dataCol]);
				const yLY = yScale(d[l1.dataCol]);

				focusLine.attr("x1", xCoord).attr("x2", xCoord).style("opacity", 1);
				focusDotTY.attr("cx", xCoord).attr("cy", yTY).style("opacity", 1);
				focusDotLY.attr("cx", xCoord).attr("cy", yLY).style("opacity", 1);

				tooltip
					.style("opacity", 1)
					.html(
						`
					<strong>${d[x.dataCol] ?? 0}</strong><br/>
					TY SALES: ${d[l2.dataCol] ?? 0}<br/>
					LY SALES: ${d[l1.dataCol] ?? 0}<br/>
					TY ASP: ${d.tyASP ?? 0}<br/>
					LY ASP: ${d.lyASP ?? 0}<br/>
					SALES G/D: ${d.salesGD ?? 0}%<br/>
					ASP G/DG: ${d.aspGDG ?? 0}%
			   `
					)
					.style("left", `${event.pageX - 50}px`)
					.style("top", `${event.pageY - 100}px`);
			})
			.on("mouseout", () => {
				focusLine.style("opacity", 0);
				focusDotTY.style("opacity", 0);
				focusDotLY.style("opacity", 0);
				tooltip.style("opacity", 0);
			});
		const legend = chart.append("g").attr("transform", "translate(50, 15)"); // above chart

		legend.append("circle").attr("cx", 0).attr("cy", 0).attr("r", 5).style("fill", l2.color);

		legend
			.append("text")
			.attr("x", 6)
			.attr("y", 4)
			.text("TY")
			.style("font-size", "10px")
			.style("font-weight", "bold")
			.style("fill", "#333");

		legend.append("circle").attr("cx", 35).attr("cy", 0).attr("r", 5).style("fill", l1.color);

		legend
			.append("text")
			.attr("x", 41)
			.attr("y", 4)
			.text("LY")
			.style("font-size", "10px")
			.style("font-weight", "bold")
			.style("fill", "#333");

		data.forEach((d) => {
			let l1val = isNaN(d[l1.dataCol]) ? 0 : +d[l1.dataCol];
			let l2val = isNaN(d[l2.dataCol]) ? 0 : +d[l2.dataCol];

			const l1valDisplay = typeof l1?.getColVal === "function" ? l1.getColVal(l1val) : l1val;
			const l2valDisplay = typeof l2?.getColVal === "function" ? l2.getColVal(l2val) : l2val;
			// TY SALES value label (hotpink)
			if (d[x.dataCol]) {
				if (l2val)
					chart
						.append("text")
						.attr("x", xScale(d[x.dataCol]) - 10)
						.attr("y", yScale(l2val) - 8) // slightly above the point
						.attr("text-anchor", "middle")
						.style("font-size", "11px")
						.style("fill", "#000")
						.text(l2valDisplay); // format as needed

				// LY SALES value label (purple)
				if (l1val)
					chart
						.append("text")
						.attr("x", xScale(d[x.dataCol]))
						.attr("y", yScale(l1val) + 14) // slightly below the point
						.attr("text-anchor", "middle")
						.style("font-size", "11px")
						.style("fill", "#000")
						.text(l1valDisplay);
			}
		});
	}, [data]);

	return (
		<Box sx={{ width: "100%" }}>
			<svg ref={svgRef} />
			<div ref={tooltipRef} className="d3_2_line_graph_tooltip" />
		</Box>
	);
};

export default LineGraphTwoLine;

// const LineGraphTwoLine = ({ data, xScale, label }) => {
// 	const svgRef = useRef();

// 	useEffect(() => {
// 		const svg = d3.select(svgRef.current);
// 		const width = 500;
// 		const height = 250;
// 		const margin = { top: 10, right: 30, bottom: 10, left: 40 };

// 		svg.selectAll("*").remove(); // clear previous chart

// 		// Scales
// 		const xScale = d3
// 			.scalePoint()
// 			.domain(data.map((d) => d.month))
// 			.range([margin.left, width - margin.right]);

// 		const yScale = d3
// 			.scaleLinear()
// 			.domain([0, d3.max(data, (d) => Math.max(d.sales1, d.sales2))])
// 			.nice()
// 			.range([height - margin.bottom, margin.top]);

// 		// Axis
// 		svg.append("g")
// 			.attr("transform", `translate(0,${height - margin.bottom})`)
// 			.call(d3.axisBottom(xScale));

// 		// svg.append("g").attr("transform", `translate(${margin.left},0)`).call(d3.axisLeft(yScale)); // no 'Cr' in tick format
// 		svg.append("foreignObject")
// 			.attr("x", 0)
// 			.attr("y", 0)
// 			.attr("width", margin.left)
// 			.attr("height", height)
// 			.append("xhtml:div")
// 			.style("display", "flex")
// 			.style("align-items", "center")
// 			.style("justify-content", "center")
// 			.style("height", "100%")
// 			.style("background", "black")
// 			.style("color", "white")
// 			.style("padding", "4px")
// 			.style("writing-mode", "sideways-lr")
// 			.style("text-orientation", "mixed")
// 			.style("font-size", "16px")
// 			.text("Sales (Cr)");
// 		// Line generators
// 		const line1 = d3
// 			.line()
// 			.x((d) => xScale(d.month))
// 			.y((d) => yScale(d.sales1));

// 		const line2 = d3
// 			.line()
// 			.x((d) => xScale(d.month))
// 			.y((d) => yScale(d.sales2));

// 		// Draw lines
// 		svg.append("path").datum(data).attr("fill", "none").attr("stroke", "#1f77b4").attr("stroke-width", 2).attr("d", line1);

// 		svg.append("path").datum(data).attr("fill", "none").attr("stroke", "#ff7f0e").attr("stroke-width", 2).attr("d", line2);

// 		// Dots and value labels for line 1
// 		// svg.selectAll(".dot1")
// 		// 	.data(data)
// 		// 	.enter()
// 		// 	.append("circle")
// 		// 	.attr("class", "dot1")
// 		// 	.attr("cx", (d) => xScale(d.month))
// 		// 	.attr("cy", (d) => yScale(d.sales1))
// 		// 	.attr("r", 4)
// 		// 	.attr("fill", "#1f77b4");

// 		svg.selectAll(".label1")
// 			.data(data)
// 			.enter()
// 			.append("text")
// 			.attr("class", "label1")
// 			.attr("x", (d) => xScale(d.month))
// 			.attr("y", (d) => yScale(d.sales1) - 10)
// 			.attr("text-anchor", "middle")
// 			.style("font-size", "12px")
// 			.style("fill", "#1f77b4")
// 			.text((d) => d.sales1);

// 		// Dots and value labels for line 2
// 		// svg.selectAll(".dot2")
// 		// 	.data(data)
// 		// 	.enter()
// 		// 	.append("circle")
// 		// 	.attr("class", "dot2")
// 		// 	.attr("cx", (d) => xScale(d.month))
// 		// 	.attr("cy", (d) => yScale(d.sales2))
// 		// 	.attr("r", 4)
// 		// 	.attr("fill", "#ff7f0e");

// 		svg.selectAll(".label2")
// 			.data(data)
// 			.enter()
// 			.append("text")
// 			.attr("class", "label2")
// 			.attr("x", (d) => xScale(d.month))
// 			.attr("y", (d) => yScale(d.sales2) - 10)
// 			.attr("text-anchor", "middle")
// 			.style("font-size", "12px")
// 			.style("fill", "#ff7f0e")
// 			.text((d) => d.sales2);

// 		// Axis labels
// 		// svg.append("text")
// 		// 	.attr("x", width / 2)
// 		// 	.attr("y", height - 10)
// 		// 	.attr("text-anchor", "middle")
// 		// 	.style("font-size", "14px")
// 		// 	.text("Month");
// 	}, [data]);

// 	return <svg ref={svgRef} width={600} height={400} />;
// };
